import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Brain, Database, PenTool, HardHat, Leaf, Building2, 
  ChevronRight, Globe, Menu, X, 
  Mail, Phone, MapPin, Send, ArrowRight, CheckCircle2,
  Cpu, BarChart3, Shield, Zap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOLUSIHIJAU_LOGO = "https://customer-assets.emergentagent.com/job_citrabi-tech/artifacts/kbqc7fcg_logo_solusihijau%20crop.png";

const HomePage = () => {
  const { language, toggleLanguage, t, setTranslations } = useLanguage();
  const [services, setServices] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({
    company_name: 'Citra Abi Permana',
    logo_url: 'https://customer-assets.emergentagent.com/job_citrabi-tech/artifacts/1bg06gil_image.png',
    address_en: 'Jakarta, Indonesia',
    address_id: 'Jakarta, Indonesia',
    email: 'info@citraabi.com',
    phone: '+62 21 1234 5678',
    whatsapp: '',
    google_maps_url: ''
  });
  const [pageContent, setPageContent] = useState({
    hero_headline_en: 'Relentless for Better.',
    hero_headline_id: 'Tanpa Henti untuk yang Lebih Baik.',
    hero_subheadline_en: 'Integrated Construction and Green Digital Solutions.',
    hero_subheadline_id: 'Solusi Konstruksi dan Digital Hijau yang Terintegrasi.',
    hero_image_left: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&q=80',
    hero_image_right: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&q=80',
    about_title_en: 'About Us',
    about_title_id: 'Tentang Kami',
    about_text_en: 'Providing Excellence in Construction and Digital Innovation. Dedicated to delivering high-quality projects and cutting-edge digital solutions that drive progress and sustainability.',
    about_text_id: 'Memberikan Keunggulan dalam Konstruksi dan Inovasi Digital. Berkomitmen untuk memberikan proyek berkualitas tinggi dan solusi digital mutakhir yang mendorong kemajuan dan keberlanjutan.',
    about_image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80',
    about_years_experience: '10+'
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    service_interest: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, translationsRes, contactRes, pageContentRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/translations`),
        axios.get(`${API}/contact-settings`),
        axios.get(`${API}/page-content`)
      ]);
      
      setServices(servicesRes.data);
      
      const translationsMap = {};
      translationsRes.data.forEach(t => {
        translationsMap[t.key] = t;
      });
      setTranslations(translationsMap);
      
      if (contactRes.data) {
        setCompanyInfo(contactRes.data);
      }
      
      if (pageContentRes.data) {
        setPageContent(pageContentRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/inquiries`, formData);
      toast.success(language === 'en' ? 'Message sent successfully!' : 'Pesan berhasil dikirim!');
      setFormData({ name: '', email: '', company: '', phone: '', message: '', service_interest: '' });
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to send message' : 'Gagal mengirim pesan');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <button onClick={() => scrollToSection('home')} className="flex items-center gap-3" data-testid="logo-link">
              <img src={companyInfo.logo_url} alt={companyInfo.company_name} className="h-10 lg:h-12 w-auto" />
              <span className="hidden sm:block font-heading font-bold text-gray-900 text-sm lg:text-base">
                {companyInfo.company_name}
              </span>
            </button>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-emerald-600 font-medium transition-colors" data-testid="nav-home">
                {language === 'en' ? 'Home' : 'Beranda'}
              </button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-emerald-600 font-medium transition-colors" data-testid="nav-about">
                {language === 'en' ? 'About Us' : 'Tentang Kami'}
              </button>
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-emerald-600 font-medium transition-colors" data-testid="nav-services">
                {language === 'en' ? 'Services' : 'Layanan'}
              </button>
              <Link to="/projects" className="text-gray-700 hover:text-emerald-600 font-medium transition-colors" data-testid="nav-projects">
                {language === 'en' ? 'Projects' : 'Proyek'}
              </Link>
              <Link to="/careers" className="text-gray-700 hover:text-emerald-600 font-medium transition-colors" data-testid="nav-careers">
                {language === 'en' ? 'Career' : 'Karir'}
              </Link>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-emerald-600 font-medium transition-colors" data-testid="nav-contact">
                {language === 'en' ? 'Contact Us' : 'Hubungi Kami'}
              </button>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-3">
              <button 
                onClick={toggleLanguage}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors text-sm font-medium"
                data-testid="language-toggle"
              >
                <Globe className="w-4 h-4" />
                {language.toUpperCase()}
              </button>

              <Link to="/admin/login" data-testid="admin-login-btn">
                <Button variant="default" className="hidden sm:flex bg-emerald-600 hover:bg-emerald-700 text-white">
                  {language === 'en' ? 'Admin Login' : 'Login Admin'}
                </Button>
              </Link>

              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-md hover:bg-gray-100"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 animate-fade-in" data-testid="mobile-menu">
            <div className="px-6 py-4 space-y-4">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left text-gray-700 hover:text-emerald-600 font-medium">
                {language === 'en' ? 'Home' : 'Beranda'}
              </button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left text-gray-700 hover:text-emerald-600 font-medium">
                {language === 'en' ? 'About Us' : 'Tentang Kami'}
              </button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left text-gray-700 hover:text-emerald-600 font-medium">
                {language === 'en' ? 'Services' : 'Layanan'}
              </button>
              <Link to="/projects" className="block text-gray-700 hover:text-emerald-600 font-medium" onClick={() => setMobileMenuOpen(false)}>
                {language === 'en' ? 'Projects' : 'Proyek'}
              </Link>
              <Link to="/careers" className="block text-gray-700 hover:text-emerald-600 font-medium" onClick={() => setMobileMenuOpen(false)}>
                {language === 'en' ? 'Career' : 'Karir'}
              </Link>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-gray-700 hover:text-emerald-600 font-medium">
                {language === 'en' ? 'Contact Us' : 'Hubungi Kami'}
              </button>
              <Link to="/admin/login" className="block" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                  {language === 'en' ? 'Admin Login' : 'Login Admin'}
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative flex items-center pt-20 pb-8 bg-gradient-to-br from-gray-50 to-white" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 lg:py-16">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Hero Content */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-gray-900 leading-tight" data-testid="hero-headline">
                  {language === 'en' ? pageContent.hero_headline_en : pageContent.hero_headline_id}
                </h1>
                <p className="text-xl lg:text-2xl text-gray-600" data-testid="hero-subheadline">
                  {language === 'en' ? pageContent.hero_subheadline_en : pageContent.hero_subheadline_id}
                </p>
              </div>
              
              <Button 
                size="lg" 
                onClick={() => scrollToSection('services')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white group" 
                data-testid="hero-cta"
              >
                {language === 'en' ? 'Our Services' : 'Layanan Kami'}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>

            {/* Hero Image - Split Composition */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="relative rounded-2xl overflow-hidden shadow-xl h-[300px] lg:h-[380px]">
                  <img 
                    src={pageContent.hero_image_left}
                    alt="Construction Professional"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/40 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="inline-block px-3 py-1 bg-emerald-600 text-white text-sm font-medium rounded-full">
                      {language === 'en' ? 'Construction' : 'Konstruksi'}
                    </span>
                  </div>
                </div>
                <div className="relative rounded-2xl overflow-hidden shadow-xl h-[300px] lg:h-[380px] mt-8">
                  <img 
                    src={pageContent.hero_image_right}
                    alt="Business Professional"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-sky-900/40 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="inline-block px-3 py-1 bg-sky-600 text-white text-sm font-medium rounded-full">
                      {language === 'en' ? 'Digital Solutions' : 'Solusi Digital'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 lg:py-20 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* About Image */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={pageContent.about_image}
                  alt="Modern Glass Building"
                  className="w-full h-[350px] lg:h-[420px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-emerald-600 text-white p-6 rounded-xl shadow-lg hidden lg:block">
                <p className="text-3xl font-bold">{pageContent.about_years_experience}</p>
                <p className="text-sm opacity-90">{language === 'en' ? 'Years Experience' : 'Tahun Pengalaman'}</p>
              </div>
            </div>

            {/* About Content */}
            <div className="space-y-6">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-gray-900" data-testid="about-title">
                {language === 'en' ? pageContent.about_title_en : pageContent.about_title_id}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                {language === 'en' ? pageContent.about_text_en : pageContent.about_text_id}
              </p>
              <div className="grid sm:grid-cols-2 gap-4 pt-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{language === 'en' ? 'Quality First' : 'Kualitas Utama'}</h4>
                    <p className="text-sm text-gray-500">{language === 'en' ? 'Excellence in every project' : 'Keunggulan di setiap proyek'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Leaf className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{language === 'en' ? 'Sustainable' : 'Berkelanjutan'}</h4>
                    <p className="text-sm text-gray-500">{language === 'en' ? 'Green solutions focus' : 'Fokus solusi hijau'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Cpu className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{language === 'en' ? 'Innovation' : 'Inovasi'}</h4>
                    <p className="text-sm text-gray-500">{language === 'en' ? 'Cutting-edge technology' : 'Teknologi mutakhir'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{language === 'en' ? 'Trusted' : 'Terpercaya'}</h4>
                    <p className="text-sm text-gray-500">{language === 'en' ? 'Reliable partnerships' : 'Kemitraan yang andal'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 lg:py-32 bg-gray-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-gray-900 mb-4">
              {language === 'en' ? 'Our Services' : 'Layanan Kami'}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {language === 'en' 
                ? 'Comprehensive solutions for construction and digital transformation'
                : 'Solusi komprehensif untuk konstruksi dan transformasi digital'}
            </p>
          </div>

          {/* IT & Digital Solutions */}
          <div className="mb-24">
            {/* Section Header */}
            <div className="text-center mb-12">
              <h3 className="font-heading font-bold text-2xl lg:text-3xl text-gray-900 mb-4">
                {language === 'en' ? 'Advanced IT & Digital Ecosystems' : 'Ekosistem IT & Digital Canggih'}
              </h3>
            </div>
            
            {/* Service Cards Grid */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* AI Card */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 bg-sky-100 rounded-xl flex items-center justify-center mb-6">
                  <Brain className="w-7 h-7 text-sky-600" />
                </div>
                <h4 className="font-heading font-bold text-xl text-gray-900 mb-3">
                  {language === 'en' ? 'Artificial Intelligence' : 'Kecerdasan Buatan'}
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  {language === 'en' 
                    ? 'Marketing Automation, AI Customer Service (Chatbots), Automated Content Creation, Data Monetization, and Recruitment Platforms.'
                    : 'Otomasi Pemasaran, Layanan Pelanggan AI (Chatbot), Pembuatan Konten Otomatis, Monetisasi Data, dan Platform Rekrutmen.'}
                </p>
              </div>

              {/* Data Warehouse Card */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 bg-sky-100 rounded-xl flex items-center justify-center mb-6">
                  <Database className="w-7 h-7 text-sky-600" />
                </div>
                <h4 className="font-heading font-bold text-xl text-gray-900 mb-3">
                  {language === 'en' ? 'Data Warehouse' : 'Gudang Data'}
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  {language === 'en' 
                    ? 'Sales & Marketing Analytics, Financial Reporting, Fraud Detection, and Performance Monitoring.'
                    : 'Analitik Penjualan & Pemasaran, Pelaporan Keuangan, Deteksi Penipuan, dan Pemantauan Kinerja.'}
                </p>
              </div>
            </div>

            {/* Powered by Solusihijau */}
            <div className="flex flex-col items-center">
              <img 
                src={SOLUSIHIJAU_LOGO}
                alt="Solusihijau.com" 
                className="h-16 w-auto mb-3"
              />
              <p className="text-gray-500 text-sm mb-2">Powered by solusihijau.com</p>
              <a 
                href="https://solusihijau.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 font-medium text-sm transition-colors"
              >
                {language === 'en' ? 'Visit Website' : 'Kunjungi Website'}
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Construction & Engineering */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left - Image */}
            <div className="relative order-2 lg:order-1">
              <div className="rounded-2xl overflow-hidden shadow-xl bg-gradient-to-br from-emerald-50 to-emerald-100 p-8">
                <img 
                  src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800&q=80" 
                  alt="Construction Planning"
                  className="w-full rounded-lg shadow-lg"
                />
              </div>
            </div>

            {/* Right - Content */}
            <div className="space-y-6 order-1 lg:order-2">
              <h3 className="font-heading font-bold text-2xl lg:text-3xl text-gray-900">
                {language === 'en' ? 'Sustainable Engineering & Construction' : 'Rekayasa & Konstruksi Berkelanjutan'}
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <HardHat className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{language === 'en' ? 'Core Engineering' : 'Rekayasa Inti'}</h4>
                    <p className="text-gray-600 text-sm">
                      {language === 'en' 
                        ? 'Planning & Design, Construction Management, and Supervisions.'
                        : 'Perencanaan & Desain, Manajemen Konstruksi, dan Pengawasan.'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Leaf className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{language === 'en' ? 'Green & Digital Solutions' : 'Solusi Hijau & Digital'}</h4>
                    <p className="text-gray-600 text-sm">
                      {language === 'en' 
                        ? 'Green Design & Efficiency Check (Energy/Carbon/Water efficiency with data tracking), Digital Construction Supervision, and Smart & Green Building Readiness.'
                        : 'Desain Hijau & Pemeriksaan Efisiensi (Efisiensi Energi/Karbon/Air dengan pelacakan data), Pengawasan Konstruksi Digital, dan Kesiapan Bangunan Pintar & Hijau.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-white mb-4">
            {language === 'en' ? 'View Our Portfolio' : 'Lihat Portofolio Kami'}
          </h2>
          <p className="text-emerald-100 mb-8 max-w-2xl mx-auto">
            {language === 'en' 
              ? 'Explore our completed projects and see how we deliver excellence.'
              : 'Jelajahi proyek yang telah selesai dan lihat bagaimana kami memberikan keunggulan.'}
          </p>
          <Link to="/projects">
            <Button size="lg" variant="outline" className="bg-white text-emerald-600 hover:bg-emerald-50 border-white">
              {language === 'en' ? 'View Projects' : 'Lihat Proyek'}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 lg:py-32 bg-gray-50" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-gray-900 mb-4" data-testid="contact-title">
                {language === 'en' ? 'Get In Touch' : 'Hubungi Kami'}
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                {language === 'en' 
                  ? "Ready to transform your business? Let's talk."
                  : "Siap mentransformasi bisnis Anda? Mari bicara."}
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{language === 'en' ? 'Address' : 'Alamat'}</h4>
                    <p className="text-gray-600">{language === 'en' ? companyInfo.address_en : companyInfo.address_id}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Email</h4>
                    <p className="text-gray-600">{companyInfo.email}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{language === 'en' ? 'Phone' : 'Telepon'}</h4>
                    <p className="text-gray-600">{companyInfo.phone}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {language === 'en' ? 'Full Name' : 'Nama Lengkap'} *
                    </label>
                    <Input
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder={language === 'en' ? 'John Doe' : 'Nama Anda'}
                      className="w-full"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {language === 'en' ? 'Email Address' : 'Alamat Email'} *
                    </label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="email@company.com"
                      className="w-full"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {language === 'en' ? 'Company Name' : 'Nama Perusahaan'}
                    </label>
                    <Input
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      placeholder={language === 'en' ? 'Your Company' : 'Nama Perusahaan'}
                      className="w-full"
                      data-testid="contact-company-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {language === 'en' ? 'Phone Number' : 'Nomor Telepon'}
                    </label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+62 xxx xxxx xxxx"
                      className="w-full"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {language === 'en' ? 'Message' : 'Pesan'} *
                  </label>
                  <Textarea
                    required
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    placeholder={language === 'en' ? 'Tell us about your project...' : 'Ceritakan tentang proyek Anda...'}
                    className="w-full"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="contact-submit-btn"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                      {language === 'en' ? 'Sending...' : 'Mengirim...'}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      {language === 'en' ? 'Send Message' : 'Kirim Pesan'}
                    </span>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-4 gap-12">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <img src={companyInfo.logo_url} alt={companyInfo.company_name} className="h-12 w-auto" />
                <span className="font-heading font-bold text-xl">{companyInfo.company_name}</span>
              </div>
              <p className="text-gray-400 mb-6 max-w-md">
                {language === 'en' 
                  ? 'Relentless for Better. Integrated Construction and Green Digital Solutions.'
                  : 'Tanpa Henti untuk yang Lebih Baik. Solusi Konstruksi dan Digital Hijau yang Terintegrasi.'}
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-heading font-semibold text-lg mb-4">{language === 'en' ? 'Quick Links' : 'Tautan Cepat'}</h4>
              <ul className="space-y-3">
                <li><button onClick={() => scrollToSection('home')} className="text-gray-400 hover:text-emerald-400 transition-colors">{language === 'en' ? 'Home' : 'Beranda'}</button></li>
                <li><button onClick={() => scrollToSection('about')} className="text-gray-400 hover:text-emerald-400 transition-colors">{language === 'en' ? 'About Us' : 'Tentang Kami'}</button></li>
                <li><button onClick={() => scrollToSection('services')} className="text-gray-400 hover:text-emerald-400 transition-colors">{language === 'en' ? 'Services' : 'Layanan'}</button></li>
                <li><Link to="/projects" className="text-gray-400 hover:text-emerald-400 transition-colors">{language === 'en' ? 'Projects' : 'Proyek'}</Link></li>
                <li><Link to="/careers" className="text-gray-400 hover:text-emerald-400 transition-colors">{language === 'en' ? 'Career' : 'Karir'}</Link></li>
                <li><button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-emerald-400 transition-colors">{language === 'en' ? 'Contact Us' : 'Hubungi Kami'}</button></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-heading font-semibold text-lg mb-4">{language === 'en' ? 'Contact' : 'Kontak'}</h4>
              <ul className="space-y-3 text-gray-400">
                <li>{language === 'en' ? companyInfo.address_en : companyInfo.address_id}</li>
                <li>{companyInfo.email}</li>
                <li>{companyInfo.phone}</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} {companyInfo.company_name}. {language === 'en' ? 'All rights reserved.' : 'Hak cipta dilindungi.'}
            </p>
            <a 
              href="https://solusihijau.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <img 
                src={SOLUSIHIJAU_LOGO}
                alt="Solusihijau.com" 
                className="h-8 w-auto opacity-70"
              />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
